import { Button, Checkbox, Label } from "@devhop/ui";
import { GripVertical, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import type { FormField } from "../types/form-builder";

type FormEditorPanelProps = {
  fields: FormField[];
  selectedFieldId: string | null;
  onSelectField: (fieldId: string) => void;
  onDeleteField: (fieldId: string) => void;
  onMoveField: (fieldId: string, direction: "up" | "down") => void;
};

export function FormEditorPanel({
  fields,
  selectedFieldId,
  onSelectField,
  onDeleteField,
  onMoveField,
}: FormEditorPanelProps) {
  const sortedFields = [...fields].sort((a, b) => a.order - b.order);

  const renderFieldPreview = (field: FormField) => {
    const baseClasses =
      "w-full rounded-md border p-3 text-sm focus:outline-none focus:ring-2";
    const selectedClasses =
      selectedFieldId === field.id
        ? "border-primary ring-2 ring-primary"
        : "border-input";

    switch (field.type) {
      case "text":
      case "email":
      case "phone":
        return (
          <input
            type={field.type === "email" ? "email" : field.type === "phone" ? "tel" : "text"}
            placeholder={field.placeholder || field.label || "ປ້ອນຂໍ້ມູນ..."}
            className={`${baseClasses} ${selectedClasses}`}
            disabled
          />
        );
      case "textarea":
        return (
          <textarea
            placeholder={field.placeholder || field.label || "ປ້ອນຂໍ້ມູນ..."}
            className={`${baseClasses} ${selectedClasses} min-h-20`}
            disabled
          />
        );
      case "number":
        return (
          <input
            type="number"
            placeholder={field.placeholder || field.label || "ປ້ອນຕົວເລກ..."}
            className={`${baseClasses} ${selectedClasses}`}
            disabled
          />
        );
      case "date":
        return (
          <input
            type="date"
            className={`${baseClasses} ${selectedClasses}`}
            disabled
          />
        );
      case "time":
        return (
          <input
            type="time"
            className={`${baseClasses} ${selectedClasses}`}
            disabled
          />
        );
      case "datetime":
        return (
          <input
            type="datetime-local"
            className={`${baseClasses} ${selectedClasses}`}
            disabled
          />
        );
      case "select":
      case "multiselect":
        return (
          <select
            multiple={field.type === "multiselect"}
            className={`${baseClasses} ${selectedClasses}`}
            disabled
          >
            <option>{field.label || "ເລືອກ..."}</option>
            {field.config.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      case "checkbox":
        return (
          <div className="flex items-center gap-2">
            <Checkbox
              id={`checkbox-${field.id}`}
              checked={false}
              disabled
            />
            <Label
              htmlFor={`checkbox-${field.id}`}
              className="cursor-pointer text-sm"
            >
              {field.label || "ການກວດສອບ"}
            </Label>
          </div>
        );
      case "radio":
        return (
          <div className="space-y-2">
            {field.config.options?.map((opt) => {
              const radioId = `radio-${field.id}-${opt.value}`;
              return (
                <div key={opt.value} className="flex items-center gap-2">
                  <input
                    type="radio"
                    id={radioId}
                    name={`radio-${field.id}`}
                    className="h-4 w-4"
                    disabled
                  />
                  <Label htmlFor={radioId} className="text-sm">
                    {opt.label}
                  </Label>
                </div>
              );
            })}
          </div>
        );
      case "file":
        return (
          <div className="flex items-center gap-2">
            <input
              type="file"
              className={`${baseClasses} ${selectedClasses}`}
              disabled
            />
          </div>
        );
      case "signature":
        return (
          <div
            className={`${baseClasses} ${selectedClasses} flex h-24 items-center justify-center border-dashed`}
          >
            <span className="text-muted-foreground text-sm">
              ພື້ນທີ່ລາຍເຊັນ
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="rounded-xl border bg-card p-6" data-tourid="form-editor-panel">
      <h3 className="mb-4 font-semibold">ຕົວຢ່າງຟອມ</h3>
      <div className="space-y-4">
        {sortedFields.length === 0 ? (
          <div className="flex h-64 items-center justify-center rounded-md border border-dashed text-muted-foreground">
            <div className="text-center">
              <p className="text-sm">ບໍ່ມີຊ່ອງຂໍ້ມູນ</p>
              <p className="text-xs">ເລືອກປະເພດຊ່ອງຂໍ້ມູນຈາກກະດານຊ້າຍ</p>
            </div>
          </div>
        ) : (
          sortedFields.map((field, index) => (
            <button
              key={field.id}
              type="button"
              className={`group relative w-full rounded-lg border p-4 text-left transition-colors ${
                selectedFieldId === field.id
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              }`}
              onClick={() => onSelectField(field.id)}
            >
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-sm">
                    {field.label || `ຊ່ອງຂໍ້ມູນ ${index + 1}`}
                    {field.required && (
                      <span className="ml-1 text-destructive">*</span>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveField(field.id, "up");
                    }}
                    disabled={index === 0}
                  >
                    <ArrowUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveField(field.id, "down");
                    }}
                    disabled={index === sortedFields.length - 1}
                  >
                    <ArrowDown className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteField(field.id);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              {renderFieldPreview(field)}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
